import { oneFromSnapshot } from "@buildwithflux/firestore-compatibility-layer";
import { EnterpriseRepository } from "./EnterpriseRepository";
export class FirestoreEnterpriseRepository extends EnterpriseRepository {
    db;
    logger;
    firestoreEnterpriseChangeSubscriptions = new Map();
    constructor(db, logger) {
        super();
        this.db = db;
        this.logger = logger;
    }
    /** @inheritDoc */
    async getByUid(uid) {
        return oneFromSnapshot(await this.db.enterprise(uid).get(), () => undefined);
    }
    /** @inheritDoc */
    async getByUidWithDefaultOrganizationAndMetadata(uid) {
        const enterprise = oneFromSnapshot(await this.db.enterprise(uid).get(), () => undefined);
        if (!enterprise) {
            return undefined;
        }
        try {
            const getDefaultOrganization = this.db.organization(enterprise.defaultOrganizationUid).get();
            const getDefaultOrganizationMetadata = this.db
                .organizationPrivateMetadata(enterprise.defaultOrganizationUid)
                .get();
            const defaultOrganization = oneFromSnapshot(await getDefaultOrganization, () => undefined);
            const defaultOrganizationMetadata = oneFromSnapshot(await getDefaultOrganizationMetadata, () => ({
                uid: enterprise.defaultOrganizationUid,
            }));
            if (!defaultOrganization) {
                this.logger.warn(`Could not find details of default organization ${enterprise.defaultOrganizationUid} for enterprise ${enterprise.uid}`);
                return undefined;
            }
            return {
                ...enterprise,
                defaultOrganization: {
                    ...defaultOrganization,
                    metadata: defaultOrganizationMetadata,
                },
            };
        }
        catch (error) {
            this.logger.error(`Could not retrieve details of default organization ${enterprise.defaultOrganizationUid} for enterprise ${enterprise.uid}`, error);
            return undefined;
        }
    }
    /** @inheritDoc */
    subscribeToEnterprise(enterpriseUid, onChange) {
        const organizationRef = this.db.enterprise(enterpriseUid);
        // NOTE: custom cleanup logic here: we check to see if there are any subscribers left.  As long as there are,
        // we keep our subscription open.  Once there aren't any remaining, we shut it down.
        const unsubscribe = this.subscriptionMgr.addSubscription(enterpriseUid, onChange, () => {
            const existingSubCount = this.subscriptionMgr.listenerCount(enterpriseUid);
            if (existingSubCount === 0) {
                const firestoreUnsub = this.firestoreEnterpriseChangeSubscriptions.get(enterpriseUid);
                if (firestoreUnsub) {
                    firestoreUnsub();
                    this.firestoreEnterpriseChangeSubscriptions.delete(enterpriseUid);
                }
            }
        });
        // NOTE: We're using a multiplexing pattern here.  There may be multiple logical subscribers to firestore,
        // but we create exactly one subscription here.  When the subscription is triggered, it causes all of the
        // logical subscriptions to fire as well.
        const existing = this.firestoreEnterpriseChangeSubscriptions.get(enterpriseUid);
        if (!existing) {
            const firestoreUnsubscriber = organizationRef.onSnapshot((docSnapshot) => {
                const data = docSnapshot.data();
                if (data === undefined) {
                    this.subscriptionMgr.notify(enterpriseUid, { type: "deleted" });
                }
                else {
                    this.subscriptionMgr.notify(enterpriseUid, {
                        type: "updated",
                        data,
                    });
                }
            }, (error) => {
                this.subscriptionMgr.notify(enterpriseUid, {
                    type: "error",
                    error,
                });
            });
            this.firestoreEnterpriseChangeSubscriptions.set(enterpriseUid, firestoreUnsubscriber);
        }
        return unsubscribe;
    }
}
//# sourceMappingURL=FirestoreEnterpriseRepository.js.map